import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, StatCard, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, WarningCircle, Phone, EnvelopeSimple, Eye, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const AGEING_LABEL = { "90-180": "90–180 days", "181-365": "181–365 days", "365+": "365+ days" };
const AGEING_VARIANT = { "90-180": "warn", "181-365": "danger", "365+": "danger" };

export default function Defaulters() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", block: "", ageing: "", sort: "outstanding_desc" });
    const [detail, setDetail] = useState({ open: false, loading: false, row: null });

    async function load() {
        setLoading(true);
        try {
            const params = {};
            for (const k of Object.keys(filters)) if (filters[k]) params[k] = filters[k];
            const [rRes, sRes, fRes] = await Promise.all([
                api.get("/defaulters", { params }),
                api.get("/defaulters/summary"),
                api.get("/flats"),
            ]);
            setRows(rRes.data);
            setSummary(sRes.data);
            setFlats(fRes.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);

    const blocks = useMemo(() => Array.from(new Set(flats.map(f => f.block))).sort(), [flats]);

    async function openDetail(row) {
        setDetail({ open: true, loading: true, row: null });
        try {
            const res = await api.get(`/defaulters/${row.flat_id}`);
            setDetail({ open: true, loading: false, row: res.data });
        } catch (e) {
            toast.error(formatError(e));
            setDetail({ open: false, loading: false, row: null });
        }
    }

    async function remindOne(row, e) {
        e.stopPropagation();
        try {
            const { data } = await api.post("/invoices/defaulters/remind", { flat_ids: [row.flat_id] });
            toast.success(`Reminder queued to ${data.emails_queued} resident(s) of ${row.flat_label}`);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function remindAll() {
        if (!confirm(`Send reminder emails to all ${rows.length} defaulter flat(s) currently listed?`)) return;
        try {
            const { data } = await api.post("/invoices/defaulters/remind", { flat_ids: rows.map(r => r.flat_id) });
            toast.success(`Reminder queued to ${data.emails_queued} resident(s) across ${data.flats_targeted} flat(s)`);
        } catch (e) { toast.error(formatError(e)); }
    }

    return (
        <div data-testid="defaulters-page">
            <PageHeader
                overline="Finance"
                title="Defaulters"
                description={`Flats with outstanding balances overdue by more than ${summary?.threshold_days ?? 90} days.`}
                actions={rows.length > 0 && (
                    <Button data-testid="remind-all-defaulters-btn" onClick={remindAll} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <PaperPlaneTilt size={14} className="mr-1.5" /> Remind all
                    </Button>
                )}
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <StatCard label="Total defaulters" value={summary?.total_defaulters ?? "—"} hint={summary ? `${summary.overdue_invoice_count} overdue invoices` : ""} />
                <StatCard label="Total outstanding" value={summary ? inr(summary.total_outstanding) : "—"} hint={summary?.oldest_days ? `Oldest ${summary.oldest_days}d` : ""} accent />
                <StatCard label="90–180 days" value={summary?.buckets?.["90-180"]?.flats ?? "—"} hint={summary ? inr(summary.buckets["90-180"].amount) : ""} />
                <StatCard label="181–365 days" value={summary?.buckets?.["181-365"]?.flats ?? "—"} hint={summary ? inr(summary.buckets["181-365"].amount) : ""} />
                <StatCard label="365+ days" value={summary?.buckets?.["365+"]?.flats ?? "—"} hint={summary ? inr(summary.buckets["365+"].amount) : ""} />
            </div>

            <div className="bg-white border border-brand-line rounded-sm p-3 mb-4 flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Search</Label>
                    <div className="relative mt-1">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="def-q" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} placeholder="Flat or resident" className="pl-7 rounded-sm border-brand-line h-9" />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Block</Label>
                    <Select value={filters.block || "any"} onValueChange={(v) => setFilters({ ...filters, block: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-28" data-testid="def-block"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">All blocks</SelectItem>
                            {blocks.map(b => <SelectItem key={b} value={b}>Block {b}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Ageing</Label>
                    <Select value={filters.ageing || "any"} onValueChange={(v) => setFilters({ ...filters, ageing: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-40" data-testid="def-ageing"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any ageing</SelectItem>
                            <SelectItem value="90-180">90–180 days</SelectItem>
                            <SelectItem value="181-365">181–365 days</SelectItem>
                            <SelectItem value="365+">365+ days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Sort</Label>
                    <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-44" data-testid="def-sort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="outstanding_desc">Highest outstanding</SelectItem>
                            <SelectItem value="oldest_first">Oldest overdue</SelectItem>
                            <SelectItem value="flat_asc">Flat number</SelectItem>
                            <SelectItem value="resident_asc">Resident name</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {(filters.q || filters.block || filters.ageing || filters.sort !== "outstanding_desc") && (
                    <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => setFilters({ q: "", block: "", ageing: "", sort: "outstanding_desc" })}>Clear</Button>
                )}
            </div>

            {loading ? <TableLoadingSkeleton rows={5} /> : rows.length === 0 ? (
                <EmptyState
                    title="No defaulters"
                    description="All outstanding balances are currently within the 90-day threshold. Nothing to chase right now."
                />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="defaulters-table">
                        <thead className="bg-brand-bg">
                            <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                <th className="px-3 py-2 text-left">Flat</th>
                                <th className="px-3 py-2 text-left">Resident</th>
                                <th className="px-3 py-2 text-right">Outstanding</th>
                                <th className="px-3 py-2 text-right">Overdue Invoices</th>
                                <th className="px-3 py-2 text-left">Oldest Due</th>
                                <th className="px-3 py-2 text-right">Days</th>
                                <th className="px-3 py-2 text-left">Ageing</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.flat_id} className="border-t border-brand-line hover:bg-brand-bg/40 cursor-pointer" onClick={() => openDetail(r)} data-testid={`def-row-${r.flat_id}`}>
                                    <td className="px-3 py-2 font-medium">{r.flat_label || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">
                                        <div>{r.resident_name || "—"}</div>
                                        {r.resident_phone && <div className="text-[10px] flex items-center gap-1"><Phone size={10} />{r.resident_phone}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{inrFull(r.outstanding)}</td>
                                    <td className="px-3 py-2 text-right">{r.overdue_invoice_count}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{r.oldest_due_date || "—"}</td>
                                    <td className="px-3 py-2 text-right">{r.max_days_overdue}</td>
                                    <td className="px-3 py-2"><Chip variant={AGEING_VARIANT[r.ageing_bucket] || "neutral"}>{AGEING_LABEL[r.ageing_bucket] || r.ageing_bucket}</Chip></td>
                                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                data-testid={`remind-defaulter-${r.flat_id}`}
                                                onClick={(e) => remindOne(r, e)}
                                                disabled={!r.resident_name}
                                                title={!r.resident_name ? "No resident linked to this flat" : "Send reminder email"}
                                                className="inline-flex items-center gap-1 text-xs font-medium text-brand-action hover:underline disabled:text-brand-inkSoft disabled:no-underline disabled:cursor-not-allowed"
                                            >
                                                <PaperPlaneTilt size={12} /> Remind
                                            </button>
                                            <button className="p-1.5 text-brand-inkSoft hover:text-brand-action" title="View detail" data-testid={`btn-detail-${r.flat_id}`}><Eye size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DetailDialog state={detail} onClose={() => setDetail({ open: false, loading: false, row: null })} />
        </div>
    );
}

function DetailDialog({ state, onClose }) {
    const { open, loading, row } = state;
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="defaulter-detail">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">
                        {row ? `${row.flat_label} · ${row.resident_name || "No resident on file"}` : "Defaulter detail"}
                    </DialogTitle>
                    <DialogDescription>
                        {row ? `${row.overdue_invoice_count} overdue invoices · oldest ${row.oldest_due_date || "—"} (${row.max_days_overdue} days)` : ""}
                    </DialogDescription>
                </DialogHeader>
                {loading && <p className="py-8 text-center text-brand-inkSoft">Loading…</p>}
                {!loading && row && (
                    <div className="space-y-4">
                        {(row.resident_phone || row.resident_email) && (
                            <div className="flex flex-wrap gap-3 text-xs">
                                {row.resident_phone && <span className="flex items-center gap-1 text-brand-ink"><Phone size={12} />{row.resident_phone}</span>}
                                {row.resident_email && <span className="flex items-center gap-1 text-brand-ink"><EnvelopeSimple size={12} />{row.resident_email}</span>}
                            </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <MetricBox label="Outstanding" value={inrFull(row.outstanding)} strong />
                            <MetricBox label="Overdue invoices" value={row.overdue_invoice_count} />
                            <MetricBox label="Oldest due" value={row.oldest_due_date || "—"} />
                            <MetricBox label="Ageing" value={AGEING_LABEL[row.ageing_bucket] || row.ageing_bucket} />
                        </div>

                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Outstanding invoices</p>
                            <div className="border border-brand-line rounded-sm overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-brand-bg">
                                        <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                            <th className="px-3 py-2 text-left">Invoice</th>
                                            <th className="px-3 py-2 text-left">Period</th>
                                            <th className="px-3 py-2 text-left">Due</th>
                                            <th className="px-3 py-2 text-right">Amount</th>
                                            <th className="px-3 py-2 text-right">Paid</th>
                                            <th className="px-3 py-2 text-right">Outstanding</th>
                                            <th className="px-3 py-2 text-right">Days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {row.invoices.map(inv => (
                                            <tr key={inv.id} className="border-t border-brand-line" data-testid={`detail-inv-${inv.id}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_ref}</td>
                                                <td className="px-3 py-2 text-brand-inkSoft">{inv.month || inv.description || "—"}</td>
                                                <td className="px-3 py-2 text-brand-inkSoft">{inv.due_date || "—"}</td>
                                                <td className="px-3 py-2 text-right">{inrFull(inv.amount)}</td>
                                                <td className="px-3 py-2 text-right">{inrFull(inv.amount_paid)}</td>
                                                <td className="px-3 py-2 text-right font-medium">{inrFull(inv.outstanding)}</td>
                                                <td className="px-3 py-2 text-right"><Chip variant={inv.days_overdue > 180 ? "danger" : "warn"}>{inv.days_overdue}d</Chip></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Recent payments</p>
                            {(!row.payments || row.payments.length === 0) ? (
                                <p className="text-xs text-brand-inkSoft py-4 text-center border border-dashed border-brand-line rounded-sm">No payment history recorded for this flat yet.</p>
                            ) : (
                                <div className="border border-brand-line rounded-sm overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-brand-bg">
                                            <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                                <th className="px-3 py-2 text-left">Date</th>
                                                <th className="px-3 py-2 text-right">Amount</th>
                                                <th className="px-3 py-2 text-left">Mode</th>
                                                <th className="px-3 py-2 text-left">Reference</th>
                                                <th className="px-3 py-2 text-left">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {row.payments.map(p => (
                                                <tr key={p.id} className={`border-t border-brand-line ${p.status === "cancelled" ? "opacity-60 line-through" : ""}`}>
                                                    <td className="px-3 py-2">{p.payment_date}</td>
                                                    <td className="px-3 py-2 text-right">{inrFull(p.amount)}</td>
                                                    <td className="px-3 py-2">{(p.payment_mode || "").replace("_", " ")}</td>
                                                    <td className="px-3 py-2 text-xs text-brand-inkSoft">{p.reference_number || p.cheque_number || "—"}</td>
                                                    <td className="px-3 py-2"><Chip variant={p.status === "confirmed" ? "success" : "danger"}>{p.status}</Chip></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MetricBox({ label, value, strong }) {
    return (
        <div className="bg-brand-bg border border-brand-line rounded-sm px-3 py-2">
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label}</p>
            <p className={`text-sm ${strong ? "font-heading text-lg text-brand-ink" : "text-brand-ink"} mt-0.5`}>{value}</p>
        </div>
    );
}
